import { Component, HostListener, Inject, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { CustomerErrorMessages } from 'src/app/constants/ErrorMessages';
import { CustomerLabelConstants } from 'src/app/constants/LabelConstants';
import { ApiUrlHelper } from 'src/app/common/api-url-helper';
import { CommonService } from 'src/app/services/common/common.service';
import { DirtyComponent } from 'src/app/component/dirty/dirty.component';
import { city, country, state } from 'src/app/models/commonModel';

@Component({
    selector: 'app-add-edit-customer-dialog',
    templateUrl: './add-edit-customer-dialog.component.html',
    styleUrls: ['./add-edit-customer-dialog.component.scss'],
    standalone: false
})
export class AddEditCustomerDialogComponent {

  // Common Properties
  CustomerId: any;
  AddCustomerForm: FormGroup;
  isEditing: boolean;
  imageSrc: string = '../../../../assets/img/default-customer-profile.svg';
  countryList: any = [];
  EditedCustomerDetails: any;
  CustomerImage: any;
  submitted: boolean = false;
  ErrorMessages: any;
  IsPreviewTrue: boolean = false;
  LabelConstants: any;
  isZipValid: boolean = false;
  protected AllCountrylist: country[] = this.countryList;
  public CountryCtrl: FormControl = new FormControl();
  protected FilteredCountry: ReplaySubject<any> = new ReplaySubject();

  @ViewChild('singleSelect', { static: true })
  singleSelect!: MatSelect;
  protected _onDestroy = new Subject();

  ngOnDestroy() {
    this._onDestroy.next(1);
    this._onDestroy.complete();
  }

  constructor(
    private dialogRef: MatDialogRef<AddEditCustomerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private api: ApiUrlHelper,
    private commonService: CommonService,
    private fb: FormBuilder,
    private dialog: MatDialog
  ) {
    this.isEditing = data.IsEdit;
    this.CustomerId = data.CustomerId;
    this.IsPreviewTrue = data.ShowPreview;
    if (this.isEditing || this.CustomerId > 0 || this.IsPreviewTrue) {
      Number.parseInt(this.CustomerId);
      this.fetchCustomerDetails();
    }
  }

  ngOnInit(): void {
    this.FilteredCountry.next(this.AllCountrylist.slice());
    this.CountryCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.FilterCountry();
    });

    this.AddCustomerForm = this.fb.group({
      CustomerImage: [''],
      FirstName: ['', Validators.required],
      LastName: ['', Validators.required],
      Email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(?=.{1,30}@)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
          ),
        ],
      ],
      PhoneNumber: ['', [Validators.required, Validators.minLength(10)]],
      Address: ['', Validators.required],
      Country: ['', Validators.required],
      State: ['', Validators.required],
      City: ['', Validators.required],
      PostalCode: ['', [Validators.required, Validators.minLength(2)]],
      HealthConcern: ['']
    });
    this.getCountryList();
    this.ErrorMessages = CustomerErrorMessages;
    this.LabelConstants = CustomerLabelConstants;
  }

  cancel() {
    if (this.AddCustomerForm.dirty) {
      const dialogRef = this.dialog.open(DirtyComponent, {
        width: '500px',
        data: {
          action: 'Customer',
        },
        disableClose: true,
        autoFocus: false
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }
  @HostListener('window:beforeunload', ['$event'])
  public canDeactivated(event: BeforeUnloadEvent): void {
    if (this.AddCustomerForm.dirty) {
      event.preventDefault();
      event.returnValue = false;
    }
  }

  isZipCodeValid(event: any) {
    let isValid = this.commonService.isZipCodeValid(event);
    if (isValid) {
      this.isZipValid = true;
    }
    else {
      this.isZipValid = false;
    }
  }

  get CustomerFormControl() {
    return this.AddCustomerForm.controls;
  }

  handleImageError() {
    this.imageSrc = '../../../assets/img/default-customer-profile.svg';
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const fileSizeInBytes = file.size;
      const maxSizeInBytes = 10 * 1024 * 1024; // 10MB (1 MB = 1024KB * 1024 Bytes)

      if (fileSizeInBytes > maxSizeInBytes) {
        this.toastr.error('Selected file exceeds the maximum allowed size (10MB).');
        return;
      }
      this.CustomerImage = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imageSrc = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  submitCustomerForm() {
    this.submitted = true;
    this.isZipCodeValid(this.AddCustomerForm.value.PostalCode);
    if (this.AddCustomerForm.valid && this.isZipValid) {
      this.AddCustomerForm.get('Email')?.enable();
      this.spinner.show();
      let api = this.api.apiUrl.Customer.AddUpdateCustomer;
      const formData = new FormData();
      formData.append('CustomerId', this.CustomerId || '0');
      formData.append('Email', this.AddCustomerForm.value.Email?.trim());
      formData.append('FirstName', this.AddCustomerForm.value.FirstName?.trim());
      formData.append('LastName', this.AddCustomerForm.value.LastName?.trim());
      formData.append('Images', this.CustomerImage);
      formData.append('PhoneNumber', this.AddCustomerForm.value.PhoneNumber?.trim());
      formData.append('CustomerAddress', this.AddCustomerForm.value.Address?.trim());
      formData.append('CountryId', this.AddCustomerForm.value.Country);
      formData.append('State', this.AddCustomerForm.value.State);
      formData.append('City', this.AddCustomerForm.value.City);
      formData.append('PostalCode', this.AddCustomerForm.value.PostalCode?.trim());
      formData.append('HealthConcernNote', this.AddCustomerForm.value.HealthConcern);
      this.commonService.doPost(api, formData).pipe().subscribe({
        next: (response) => {
          if (response.success) {
            this.isZipValid = true;
            this.toastr.success(response.message);
            this.dialogRef.close(true);
            this.spinner.hide();
          } else {
            this.spinner.hide();
            this.toastr.error(response.message);
          }
        },
        error(err) {
          this.spinner.hide();
          console.log(err);
        },
      });
    }
  }

  getCountryList() {
    let api = this.api.apiUrl.Country.GetCountryList;
    this.FilteredCountry.next([]);
    this.AllCountrylist = [];
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.AllCountrylist = response.data.map((item) => ({
            id: item.countryId,
            value: item.countryname,
          }));
          this.FilterCountry();
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.log(err);
      }
    });
  }

  fetchCustomerDetails() {
    this.spinner.show();
    let api = this.api.apiUrl.Customer.GetCustomerById.replace('{Id}', this.CustomerId);
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        this.EditedCustomerDetails = response.data;
        this.imageSrc = this.EditedCustomerDetails.images;
        this.AddCustomerForm.patchValue({
          UserImage: this.EditedCustomerDetails.images || this.imageSrc,
          FirstName: this.EditedCustomerDetails.firstName,
          LastName: this.EditedCustomerDetails.lastName,
          Email: this.EditedCustomerDetails.email,
          PhoneNumber: this.EditedCustomerDetails.phoneNumber,
          Address: this.EditedCustomerDetails.customerAddress,
          Country: this.EditedCustomerDetails.countryId,
          State: this.EditedCustomerDetails.state,
          City: this.EditedCustomerDetails.city,
          PostalCode: this.EditedCustomerDetails.postalCode,
          HealthConcern: this.EditedCustomerDetails.healthConcernNote
        });
        this.spinner.hide();
        this.AddCustomerForm.get('Email')?.disable();
        if (this.IsPreviewTrue) {
          this.AddCustomerForm.disable();
        }
      },
      error(err) {
        this.spinner.hide();
        console.log(err);
      },
    });
  }

  protected FilterCountry() {
    if (!this.AllCountrylist) {
      return;
    }
    let search = this.CountryCtrl.value;
    if (!search) {
      this.FilteredCountry.next(this.AllCountrylist.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.FilteredCountry.next(
      this.AllCountrylist.filter(
        (data) => data.value.toLowerCase().indexOf(search) > -1
      )
    );
  }
}
